import { cn } from '@/lib/utils'

interface PillComposerProps extends React.ComponentProps<'div'> {
  /** Controls left of the field (attach). */
  leading?: React.ReactNode
  /** Controls right of the field (mic, voice or send). */
  trailing?: React.ReactNode
  /** What the message is about (a context chip), shown above the field. */
  context?: React.ReactNode
}

/** The rounded composer shell: leading controls, the text field, trailing
 * controls. Each side is one 44px line tall, so its buttons centre on the
 * field's first line and stay at the bottom as the field grows. Owns layout
 * only; the caller owns the field and its behaviour. */
function PillComposer({
  leading,
  trailing,
  context,
  className,
  children,
  ...props
}: PillComposerProps) {
  return (
    <div
      data-slot="pill-composer"
      className={cn(
        'border-input bg-background shadow-pill focus-within:border-ring rounded-[28px] border',
        className
      )}
      {...props}
    >
      {context && <div className="px-3 pt-2">{context}</div>}
      <div className="flex min-h-14 items-end gap-2 px-1.5 py-1.5 md:px-2.5">
        {leading && (
          <div className="flex h-11 shrink-0 items-center gap-1 md:gap-1.5">{leading}</div>
        )}
        <div className="flex min-h-11 min-w-0 flex-1 items-center self-stretch">{children}</div>
        {trailing && (
          <div className="flex h-11 shrink-0 items-center gap-1 md:gap-1.5">{trailing}</div>
        )}
      </div>
    </div>
  )
}

const COMPOSER_BUTTON =
  'flex size-11 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40 md:size-9 [&_svg]:size-5 md:[&_svg]:size-[18px]'

/** Circular composer control, one size for all: `subtle` is an icon-only button (attach, camera,
 * mic), `solid` the filled primary action (voice, send). 44px on a phone, 36px on a wide screen. */
function ComposerButton({
  tone,
  className,
  ...props
}: React.ComponentProps<'button'> & { tone: 'subtle' | 'solid' }) {
  return (
    <button
      type="button"
      className={cn(
        COMPOSER_BUTTON,
        tone === 'subtle'
          ? 'text-foreground hover:bg-foreground/8'
          : 'bg-foreground text-background hover:bg-foreground/85',
        className
      )}
      {...props}
    />
  )
}

export { PillComposer, ComposerButton }
